import {Injectable} from '@angular/core';

import {AngularFireDatabase} from '@angular/fire/compat/database';

import {Observable} from 'rxjs';
import {BlindtestService} from '../blindtest/blindtest.service';
import {GameService} from '../game/game.service';
import {ReviewService} from '../review/review.service';
import {ScoreService} from '../score/score.service';
import {StatsService} from '../stats/stats.service';
import {HttpClient} from '@angular/common/http';
import {ErrorHandler} from '../../handler/error-handler';
import {LoggerService} from '../logger/logger.service';
import {OrganizationService} from '../organization/organization.service';
import {GameControls} from '@frogconnexion/blinding-common';

@Injectable()
export class GameControlsService {
  private _currentGameStateObservable: Observable<GameControls>;
  private _currentGameState: GameControls;
  private _organization: string;

  constructor(private _fb: AngularFireDatabase,
              private _blindingService: OrganizationService,
              private _blindtestService: BlindtestService,
              private _gameService: GameService,
              private _scoreService: ScoreService,
              private _reviewService: ReviewService,
              private _statsService: StatsService,
              private _http: HttpClient,
              private _logger: LoggerService,
              private _errorHandler: ErrorHandler) {

    this._blindingService.organizationTag().subscribe(o => {
      this._organization = o;
    });
    // Current Game State observable
    this._currentGameStateObservable = this._gameService.currentGameControls();
    this._currentGameStateObservable.subscribe(g => {
      // Replay all game-specific observables
      this._currentGameState = g;
    });

  }

  // Public Async methods

  startSet(index: number): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot start set: No game is currently set.');
    }
    if (index < 0 || index + 1 > this._currentGameState.blindtestControl.setCount) {
      throw new Error('Wrong index value. Must be between 0 and ' + (this._currentGameState.blindtestControl.setCount - 1));
    }
    // Set status of target set to OnGoing
    return this._http.post<void>(`/admin/org/${this._organization}/game/set/${index}/start`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  lockSong(): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot lock song: No game is currently set.');
    }
    // Set status of target set to OnGoing
    return this._http.post<void>(`/admin/org/${this._organization}/game/song/lock`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  revealSong(): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot reveal song: No game is currently set.');
    }
    // Set status of target set to OnGoing
    return this._http.post<void>(`/admin/org/${this._organization}/game/song/reveal`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  resetSong(): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot reset song: No game is currently set.');
    }
    // Reset current song in case error occurred
    return this._http.post<void>(`/admin/org/${this._organization}/game/song/reset`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  nextSong(): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot switch to next song: No game is currently set.');
    }
    // Set status of target set to OnGoing
    return this._http.post<void>(`/admin/org/${this._organization}/game/song/next`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  previousSong(): Observable<any> {
    if (!this._currentGameState) {
      throw new Error('Cannot switch to previous song: No game is currently set.');
    }
    // Set status of target set to OnGoing
    return this._http.post<void>(`/admin/org/${this._organization}/game/song/previous`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  setPreviewLeaderboard(show: boolean): Observable<void> {
    return this._http.post<void>(`/admin/org/${this._organization}/game/leaderboard/preview/${show}`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  revealLeaderboard(show: boolean): Observable<void> {
    return this._http.post<void>(`/admin/org/${this._organization}/game/leaderboard/reveal/${show}`, null)
        .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  setEnableJokers(enable: boolean) {
    return this._http.post<void>(`/admin/org/${this._organization}/game/jokers/enable/${enable}`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

  setLockPlayers(lock: boolean) {
    return this._http.post<void>(`/admin/org/${this._organization}/game/players/lock/${lock}`, null)
      .pipe(this._errorHandler.retryThreeTimesOrError());
  }

}
